import { CommonModule, Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { cnpj, cpf } from 'cpf-cnpj-validator';
import { ConfirmationService, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { FormFieldComponent } from '../../../shared/components/form-field/form-field.component';
import { PessoaEditModel } from '../../models/pessoa-Edit.Model';
import { I18nService } from '../../service/i18n.service';
import { NotificacaoService } from '../../service/notificacao.service';
import { ToolsService } from '../../service/tools.service';
import { CadastroService } from '../cadastro/service/cadastro.service';

@Component({
  selector: 'app-editar',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    FormFieldComponent,
    ButtonModule,
    InputTextModule,
    SharedModule,
    PasswordModule,
    InputMaskModule,
    ConfirmDialogModule,
    FloatLabelModule,
    FileUploadModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './editar.component.html',
  styleUrl: './editar.component.scss',
})
export class EditarComponent {
  form!: FormGroup;
  isBotaoSalvar: boolean = false;
  mascaraCpfCnpj = signal('9999.999.999-99');
  botaoLoading = signal(false);
  usuario: PessoaEditModel = {} as PessoaEditModel;
  filePreviewUrl: string | undefined = undefined;

  //injecao de dependencia
  fb = inject(FormBuilder);
  usuarioService = inject(CadastroService);
  http = inject(HttpClient);
  translate = inject(TranslateService);
  i18nService = inject(I18nService);
  router = inject(Router);
  notificacaoService = inject(NotificacaoService);
  confirmationService = inject(ConfirmationService);
  location = inject(Location);

  constructor() {
    this.translate.use(this.i18nService.idioma.value.code);
  }

  ngOnInit() {
    this.preencherFormulario();
  }

  preencherFormulario() {
    this.form = this.fb.group({
      nome: ['', [Validators.required, this.nomeSobrenomeValidator]],
      cpfCnpj: ['', [Validators.required, this.cpfCnpjValidator]],
      email: ['', [Validators.required, Validators.email]],
      telefone: ['', Validators.required],
      website: [''],
      redeSocial: [''],
      imagemPerfil: [''],
      typeImage: [''],
      endereco: this.fb.group({
        logradouro: [''],
        numero: [''],
        bairro: [''],
        cidade: [''],
        estado: [''],
        complemento: [''],
        pais: [''],
        codPostal: ['', Validators.required],
        latitude: [''],
        longitude: [''],
      }),
    });

    this.usuarioService.buscarUsuario().then((usuario) => {
      this.usuario = usuario as PessoaEditModel;

      // Atualiza o formulário com os valores do usuário
      this.form.patchValue(usuario);

      // Verifica se existe uma imagem associada ao usuário
      if (usuario.imagemPerfil) {
        // Cria a URL Base64 para exibição
        this.filePreviewUrl = `data:${
          usuario.typeImage || 'image/png'
        };base64,${usuario.imagemPerfil}`;
      }
    });
  }

  // Validação para nome e sobrenome
  nomeSobrenomeValidator(control: any) {
    const nomeCompleto = control.value.trim();
    if (nomeCompleto.split(' ').length < 2) {
      return { nomeIncompleto: true };
    }
    return null;
  }

  // Validação de CPF ou CNPJ
  cpfCnpjValidator(control: any) {
    const valor = control.value;
    if (!cpf.isValid(valor) && !cnpj.isValid(valor)) {
      return { cpfCnpjInvalido: true };
    }
    return null;
  }

  validarMascaraCpfCnpj(event: Event) {
    const valor = ToolsService.removerMascara(
      (event.target as HTMLInputElement).value
    );

    if (valor.length <= 11) {
      (event.target as HTMLInputElement).value = this.formatarCPF(valor);
    } else {
      (event.target as HTMLInputElement).value = this.formatarCNPJ(valor);
    }
  }

  formatarCPF(cpf: string): string {
    // Aplica a máscara de CPF: 000.000.000-00
    return cpf
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  formatarCNPJ(cnpj: string): string {
    // Aplica a máscara de CNPJ: 00.000.000/0000-00
    return cnpj
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }

  // Autocomplete de endereço via CEP
  buscarCep() {
    const cep = this.form.get('endereco.codPostal')?.value;
    if (cep) {
      this.usuarioService.buscarCep(cep).then((data: any) => {
        if (data) {
          this.form.patchValue({
            endereco: {
              logradouro: data.logradouro,
              bairro: data.bairro,
              cidade: data.localidade,
              estado: data.uf,
              pais: 'Brasil',
            },
          });
        }
      });
    }
  }

  validarClasseErro(campo: string): string {
    return this.validarCampo(campo) ? 'cs-input--error' : '';
  }

  validarCampo(campo: string): boolean | undefined | null {
    let campoSimples = this.form.get(campo);
    let campoComplexo = this.form.get(campo)?.get('valor');

    return (
      (campoSimples?.errors || campoComplexo?.errors) &&
      (campoSimples?.dirty || campoComplexo?.dirty || this.isBotaoSalvar)
    );
  }

  removerEspacos(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);

      if (control instanceof FormControl && typeof control.value === 'string') {
        const trimmed = control.value.replace(/>\s+|\s+</g, (m) => m.trim());
        control.setValue(trimmed.trim(), { emitEvent: true });
      } else if (control instanceof FormGroup) {
        this.removerEspacos(control);
      }
    });
  }

  onSubmit() {
    this.removerEspacos(this.form);
    this.isBotaoSalvar = true;

    if (this.form.valid) {
      this.notificacaoService.notificacaoWarn(
        this.translate.instant(
          'PAGINA_EDITAR_CONTA.MENSSAGEM_NOTIFICACAO_ALTERANDO_USUARIO',
          1000
        )
      );
      this.botaoLoading.set(true);
      this.usuarioService
        .atualizarUsuario(this.montarUsuario())
        .then((resp) => {
          this.notificacaoService.notificacaoSuccess(
            this.translate.instant(
              'PAGINA_EDITAR_CONTA.MENSSAGEM_NOTIFICACAO_ALTERADO_USUARIO',
              1000
            )
          );
        })
        .catch((err) => {
          const error = this.translate.instant(
            'PAGINA_EDITAR_CONTA.CONSULTE_SUPORTE'
          );
          this.notificacaoService.notificacaoError(
            `Erro: ${err.status} ${error}`
          );
        })
        .finally(() => {
          setTimeout(() => {
            this.router.navigate(['/selecao-sistemas']);
          }, 2000);
        });
    }
  }

  montarUsuario() {
    const usuario: PessoaEditModel = {
      id: this.usuario.id,
      nome: this.form.get('nome')?.value,
      cpfCnpj: ToolsService.removerMascara(this.form.get('cpfCnpj')?.value),
      email: this.form.get('email')?.value,
      telefone: ToolsService.removerMascara(this.form.get('telefone')?.value),
      website: this.form.get('website')?.value,
      redeSocial: this.form.get('redeSocial')?.value,
      idioma: null,
      imagemPerfil: this.form.get('imagemPerfil')?.value,
      typeImage: this.form.get('typeImage')?.value,
      endereco: {
        id: this.usuario.endereco.id,
        logradouro: this.form.get('endereco.logradouro')?.value,
        numero: this.form.get('endereco.numero')?.value,
        bairro: this.form.get('endereco.bairro')?.value,
        cidade: this.form.get('endereco.cidade')?.value,
        estado: this.form.get('endereco.estado')?.value,
        complemento: this.form.get('endereco.complemento')?.value,
        pais: this.form.get('endereco.pais')?.value,
        codPostal: ToolsService.removerMascara(
          this.form.get('endereco.codPostal')?.value
        ),
        latitude: null,
        longitude: null,
      },
    };
    return usuario;
  }

  // Atualizar controle com a foto selecionada
  imagemSelecionada(event: any, fileUpload: any): void {
    const file = event.files[0]; // Obtém o arquivo enviado

    if (file) {
      const reader = new FileReader();

      // Verifica o tamanho do arquivo (3MB = 3145728 bytes)
      if (file.size > 3 * 1024 * 1024) {
        this.notificacaoService.notificacaoWarn(
          'O tamanho da imagem não pode exceder 3 MB'
        );
        fileUpload.clear(); // Limpa o campo de upload
        return;
      }

      // Lê o arquivo como Data URL (Base64)
      reader.onload = () => {
        const base64String = reader.result as string; // Converte o resultado para string Base64

        // Define os valores no formulário
        this.form.get('imagemPerfil')?.setValue(base64String.split(',')[1]); // Remove o prefixo 'data:image/*;base64,'
        this.form.get('typeImage')?.setValue(file.type); // Salva o tipo MIME da imagem

        // Cria a pré-visualização a partir da Base64
        this.filePreviewUrl = base64String;
      };

      reader.readAsDataURL(file); // Lê o arquivo como Data URL (Base64)
    }

    fileUpload.clear(); // Limpa o menu
  }

  removerImagem() {
    this.filePreviewUrl = undefined;
    this.form.get('imagemPerfil')?.setValue('');
    this.form.get('typeImage')?.setValue('');
  }

  cancelar() {
    this.location.back();
  }
}
